import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject, map, Observable } from "rxjs";
import { ApiService } from "./api.service";
import { environment } from "@env/environment";
import {
    IAcciones,
    IFuncionalidades,
    ITerminologia,
    IUsuario,
} from "@models/usuario.interface";
import { Usuario } from "@models/usuario.model";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import { DangerError } from "@shared/errors/custom.error";
import IRegion from "@models/region.interface";
import IEstado, { IEstadoExpediente } from "@models/estado.interface";
import IMotivo from "@models/motivo.interface";
import { ITipoDocumento, ITipoNotificacion } from "@models/tipo.interface";
import Roles from "@shared/enums/roles.enum";
import { IResponseAPIObjeto } from "@models/responseAPI.interface";
import IRoles from "@models/roles.interface";
import IMensaje from "@models/mensaje.interface";



@Injectable({
    providedIn: "root",
})
export class SessionService {
    private sesionSubject = new BehaviorSubject<Usuario | null>(null);
    sesion$ = this.sesionSubject.asObservable();

    private sessionChannel = new BroadcastChannel("sesion-channel");
    private isLeader = false;
    isLoadingChangeRole: boolean = false; // Variable para saber cuando se esta realizando un cambio de rol

    constructor(private apiService: ApiService, private ngZone: NgZone) {
        // ⬇️ DEV BYPASS: si deshabilitamos auth, creamos sesión FAKE y salimos
        if (environment.disableAuth === true) {
            console.warn("[DEV] disableAuth=TRUE → sesión FAKE creada, sin sync entre pestañas.");
            const fake = assignToModel(Usuario, {
                id_sesion: "FAKE",
                rut_usuario: "11111111-1",
                nombres: "Dev",
                ape_paterno: "User",
                ape_materno: "",
                mail_usuario: "dev@example.com",
                token: "FAKE_TOKEN",
                creacion: new Date().toISOString(),
                expiracion: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
                aplicacion: "FAKE",
                perfiles: [],
                roles: "1", // un rol cualquiera
                cobertura: [],
                token_cu: "",
                permisos: { Funcionalidades: [] },
                terminologia: {},
                rolSeleccionado: 1 as Roles,
            });
            // setSesion salta terminología en DEV (ver más abajo)
            this.setSesion(fake);
            return; // no iniciar listeners ni sync entre pestañas
        }

        // Flujo normal (PROD)
        this.listenToTabs();
        this.checkIfLeader();
        this.initSesion();

        window.addEventListener("beforeunload", () => {
            this.sessionChannel.postMessage({ type: "tab-closed" });
            this.handleBeforeUnload();
        });
    }

    get rolActual(): number {
        return this.isAuthenticated() && this.getSesionActual()?.rolSeleccionado;
    }

    private initSesion(): void {
        // ⬇️ En DEV ya retornamos en el constructor
        const id_sesion = sessionStorage.getItem("id_sesion");
        if (id_sesion) {
            const expiracion = sessionStorage.getItem("expiracion");
            if (expiracion && new Date(expiracion) < new Date()) {
                sessionStorage.clear();
                return;
            }
            this.restoreSesionFromStorage();
        } else {
            console.log("Pestaña sin sesión: solicitando a otras pestañas...");
            this.sessionChannel.postMessage({ type: "request-sesion" });

            const timeout = setTimeout(() => {
                if (!this.sesionSubject.value) {
                    console.warn(
                        "Ninguna pestaña respondió con sesión. Usuario no autenticado."
                    );
                }
            }, 500);

            (this as any)._sesionTimeout = timeout;
        }
    }

    private restoreSesionFromStorage(): void {
        this.sesionSubject.next(
            assignToModel(Usuario, {
                id_sesion: sessionStorage.getItem("id_sesion") || "",
                rut_usuario: sessionStorage.getItem('rut_usuario') || '',
                nombres: sessionStorage.getItem("nombres") || "",
                ape_paterno: sessionStorage.getItem("ape_paterno") || "",
                ape_materno: sessionStorage.getItem("ape_materno") || "",
                mail_usuario: sessionStorage.getItem("mail_usuario") || "",
                token: sessionStorage.getItem("token") || "",
                creacion: sessionStorage.getItem("creacion") || "",
                expiracion: sessionStorage.getItem("expiracion") || "",
                aplicacion: sessionStorage.getItem("aplicacion") || "",
                perfiles: sessionStorage.getItem("perfiles") || "",
                roles: sessionStorage.getItem("roles") || "",
                cobertura: sessionStorage.getItem("cobertura") || "",
                token_cu: sessionStorage.getItem("token_cu") || "",
                permisos: JSON.parse(sessionStorage.getItem("permisos")) || {},
                terminologia: JSON.parse(sessionStorage.getItem("terminologia")) || {},
                rolSeleccionado: Number(sessionStorage.getItem("rolSeleccionado")) || null,
            })
        );
        // ⬇️ No pedir terminología en DEV (evitamos llamada a API)
       
    }

    async setSesion(sesion: Usuario): Promise<void> {
        sessionStorage.setItem("id_sesion", sesion.id_sesion);
        sessionStorage.setItem("rut_usuario", sesion.rut_usuario);
        sessionStorage.setItem("nombres", sesion.nombres);
        sessionStorage.setItem("ape_paterno", sesion.ape_paterno);
        sessionStorage.setItem("ape_materno", sesion.ape_materno);
        sessionStorage.setItem("mail_usuario", sesion.mail_usuario);
        sessionStorage.setItem("token", sesion.token?.replace(/'/g, '') || '');
        sessionStorage.setItem("creacion", sesion.creacion);
        sessionStorage.setItem("expiracion", sesion.expiracion);
        sessionStorage.setItem("aplicacion", String(sesion.aplicacion));
        sessionStorage.setItem("perfiles", sesion.perfiles || '');
        sessionStorage.setItem("roles", sesion.roles || '');
        sessionStorage.setItem("cobertura", sesion.cobertura || '');
        sessionStorage.setItem("token_cu", sesion.token_cu);
        if (sesion.permisos && typeof sesion.permisos === 'object' && "Funcionalidades" in sesion.permisos) sessionStorage.setItem("permisos", JSON.stringify(sesion.permisos));
        sessionStorage.setItem("rolSeleccionado", "");
        if (sesion.rolSeleccionado) sessionStorage.setItem("rolSeleccionado", sesion.rolSeleccionado.toString());
        else if (sesion.roles.split(",").length === 1) {
            sesion.rolSeleccionado = sesion.roles.split(",").map(Number)[0];
            sessionStorage.setItem("rolSeleccionado", sesion.roles.split(",")[0]);
        }

        this.sesionSubject.next(assignToModel(Usuario, sesion));

        // ⬇️ No pedir terminología en DEV (evitamos API)
       
    }

    actualizarFechasSesion(expiracion: string, creacion?: string): void {
        const sesion = this.getSesionActual();
        if (!sesion) return;
        if (expiracion) {
            sesion.expiracion = expiracion;
            sessionStorage.setItem("expiracion", expiracion);
        }
        if (creacion) {
            sesion.creacion = creacion;
            sessionStorage.setItem("creacion", creacion);
        }
        this.sesionSubject.next(sesion);
    }

    setRolSeleccionado(rol: Roles | null) {
        const sesion = this.getSesionActual();
        sesion.rolSeleccionado = rol;
        sessionStorage.setItem("rolSeleccionado", rol?.toString()??"");
    }

    getSesionActual(): Usuario | null {
        return this.sesionSubject.getValue();
    }

    clearSesion(): void {
        sessionStorage.clear();
        this.sesionSubject.next(null);
    }

    getToken(): string | null {
        const token = sessionStorage.getItem("token");
        return token ? token : (sessionStorage.getItem("id_sesion") || null);
    }

    // ⬇️ DEV: siempre autenticado
    isAuthenticated(): boolean {
        if (environment.disableAuth) return true;
        const token = this.getToken();
        return !!token;
    }

    // ⬇️ DEV: todas las funcionalidades permitidas
    hasFunctionality(funcionalidad: number): boolean {
        if (environment.disableAuth) return true;
        return (
            this.isAuthenticated() &&
            this.getSesionActual()?.permisos?.Funcionalidades?.some(
                (f) => f.codigo_fnc === funcionalidad
            )
        );
    }

    // Verificar permisos de forma reactiva
    hasFunctionality$(idFuncionalidad: number): Observable<boolean> {
        if (environment.disableAuth) return new BehaviorSubject<boolean>(true).asObservable();
        return this.sesionSubject.asObservable().pipe(
            map(s => !!s?.permisos?.Funcionalidades?.some(
                (f) => f.codigo_fnc === idFuncionalidad
            ))
        );
    }

    // ⬇️ DEV: devolver objeto "dummy" con nombres para que no queden vacíos
    getFunctionality(funcionalidad: number): IFuncionalidades {
        if (environment.disableAuth) {
            const pretty = String(funcionalidad);
            return {
                // @ts-ignore: devolver mínimo necesario
                codigo_fnc: funcionalidad,
                nombre_fnc: pretty,
                ayuda_fnc: pretty,
                Acciones: [],
            } as unknown as IFuncionalidades;
        }
        return (
            this.isAuthenticated() &&
            this.getSesionActual()?.permisos?.Funcionalidades?.find(
                (f) => f.codigo_fnc === funcionalidad
            )
        );
    }

    // ⬇️ DEV: todas las acciones permitidas
    hasAction(funcionalidad: number, accion: number): boolean {
        if (environment.disableAuth) return true;
        return (
            this.isAuthenticated() &&
            this.getFunctionality(funcionalidad)?.Acciones?.some(
                (a) => a.codigo_acc === accion
            )
        );
    }

    getAction(funcionalidad: number, accion: number): IAcciones {
        if (environment.disableAuth) {
            return { /* dummy */ } as unknown as IAcciones;
        }
        return (
            this.isAuthenticated() &&
            this.getFunctionality(funcionalidad)?.Acciones?.find(
                (a) => a.codigo_acc === accion
            )
        );
    }

    getRoles(): Roles[] {
        if (environment.disableAuth) return [1 as unknown as Roles];
        return (
            this.isAuthenticated() ?
            this.getSesionActual()
                ?.roles.split(",")
                .map(Number) : []
        );
    }

    isMultiRol(): boolean {
        if (environment.disableAuth) return false;
        return this.isAuthenticated() && this.getSesionActual().roles.split(",").length > 1;
    }

    isFuncionarioEvaluado(): boolean {
        if (environment.disableAuth) return false;
        return this.rolActual === Roles.funcionario || this.getRoles().includes(Roles.funcionario);
    }

    // BroadcastChannel: Comunicación entre pestañas (solo PROD)
    private listenToTabs(): void {
        if (environment.disableAuth) return; // ⬅️ no escuchar en DEV

        this.sessionChannel.postMessage({ type: "tab-opened" });

        this.sessionChannel.onmessage = (event) => {
            this.ngZone.run(() => {
                const data = event.data;

                if (data.type === "tab-opened") {
                    this.isLeader = false;
                } else if (data.type === "tab-closed") {
                    this.checkIfLeader();
                } else if (data.type === "request-sesion") {
                    const currentSesion = this.getSesionActual();
                    if (currentSesion) {
                        this.sessionChannel.postMessage({
                            type: "send-sesion",
                            sesion: currentSesion,
                        });
                    }
                } else if (data.type === "send-sesion" && data.sesion) {
                    if (!this.sesionSubject.value) {
                        clearTimeout((this as any)._sesionTimeout);
                        this.setSesion(data.sesion);
                    }
                }
            });
        };
    }

    private checkIfLeader(): void {
        if (environment.disableAuth) return;
        setTimeout(() => {
            if (!this.isLeader) {
                this.isLeader = true;
            }
        }, 100);
    }

    private async handleBeforeUnload(): Promise<void> {
        if (environment.disableAuth) return; // ⬅️ no llamar logout en DEV

        if (this.isLeader) {
            const sesion = this.getSesionActual();
            if (sesion?.id_sesion && sesion.aplicacion !== "FAKE") {
                const url = environment.backendUrl + environment.logout;
                const payload = { id: sesion.id_sesion };

                try {
                    await this.apiService.fireAndForgetPost(url, payload);
                } catch (e) {
                    console.error("Error enviando logout en beforeunload:", e);
                }
            }
        }
    }

}
